import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

import { pluriel } from '@/lib/pluriel';
import { AdminButton } from "@/components/admin/button";
import { Card, CardBody, CardHead } from "@/components/admin/card";
import { FormRow } from "@/components/admin/form";
import { AdminIcon } from "@/components/admin/icon";
import { PageHead } from "@/components/admin/page-head";
import { Tag } from "@/components/admin/tag";
import { useToast } from "@/components/ui/feedback";
import { extractErrorMessage } from "@/hooks/use-auth-api";
import { usePageTitle } from "@/hooks/use-page-title";
import {
	marketingApi,
	type MarketingDraft,
	type MarketingType,
	type PublicationContent,
	type PubliciteContent,
	type VisualFormat,
} from "@/lib/marketing/marketing-api";
import type { TagClass } from "@/lib/status";
import { useMarketingCart } from "@/stores/marketing-cart.store";

type Mode = "grouped" | "per_product";

const TONE_OPTIONS = [
	{ value: "", label: "Ton : auto" },
	{ value: "nouveaute", label: "Nouveauté" },
	{ value: "promo", label: "Promotion / bon plan" },
	{ value: "reassort", label: "Réassort" },
	{ value: "conseil", label: "Conseil / éducatif" },
];

const FORMAT_OPTIONS: { value: VisualFormat; label: string }[] = [
	{ value: "square", label: "Carré 1:1" },
	{ value: "portrait", label: "Portrait 4:5 / story" },
	{ value: "landscape", label: "Paysage 1.91:1" },
];

function statusTag(status: MarketingDraft["status"]): { label: string; cls: TagClass } {
	switch (status) {
		case "queued":
			return { label: "En file", cls: "neutral" };
		case "generating":
			return { label: "Génération…", cls: "warning" };
		case "draft":
			return { label: "Brouillon", cls: "success" };
		case "failed":
			return { label: "Échec", cls: "danger" };
		case "published":
			return { label: "Publié", cls: "info" };
		default:
			return { label: status, cls: "info" };
	}
}

export function AdminMarketingGeneratorPage() {
	usePageTitle("Générateur marketing");
	const toast = useToast();
	const qc = useQueryClient();

	const items = useMarketingCart((s) => s.items);
	const removeItem = useMarketingCart((s) => s.remove);
	const clearCart = useMarketingCart((s) => s.clear);

	// Paramètres de génération
	const [type, setType] = useState<MarketingType>("publication");
	const [mode, setMode] = useState<Mode>("grouped");
	const [platforms, setPlatforms] = useState<string[]>(["facebook", "instagram"]);
	const [tone, setTone] = useState("");
	const [brief, setBrief] = useState("");

	// Résultat regroupé (éditable)
	const [streaming, setStreaming] = useState(false);
	const [liveText, setLiveText] = useState("");
	const [hasResult, setHasResult] = useState(false);
	const [pubMessage, setPubMessage] = useState("");
	const [pubHashtags, setPubHashtags] = useState("");
	const [adPrimary, setAdPrimary] = useState("");
	const [adHeadline, setAdHeadline] = useState("");
	const [adDescription, setAdDescription] = useState("");
	const [adCta, setAdCta] = useState("");

	const status = useQuery({
		queryKey: ["marketing", "status"],
		queryFn: () => marketingApi.status(),
	});
	const imageEnabled = status.data?.imageEnabled ?? false;
	const textEnabled = status.data?.textEnabled ?? false;

	const draftsQuery = useQuery({
		queryKey: ["marketing", "drafts"],
		queryFn: () => marketingApi.list({ pageSize: 50 }),
		refetchInterval: (q) => {
			const rows = q.state.data?.data ?? [];
			return rows.some((d) => d.status === "queued" || d.status === "generating")
				? 2500
				: false;
		},
	});

	const togglePlatform = (p: string) =>
		setPlatforms((cur) =>
			cur.includes(p) ? cur.filter((x) => x !== p) : [...cur, p],
		);

	const basePayload = () => ({
		type,
		productIds: items.map((i) => i.id),
		platforms: type === "publication" ? platforms : undefined,
		tone: tone || undefined,
		brief: brief.trim() || undefined,
	});

	async function generateGrouped() {
		if (!items.length) return;
		setStreaming(true);
		setLiveText("");
		setHasResult(false);
		try {
			const r = await marketingApi.generateStream(basePayload(), (d) =>
				setLiveText((t) => t + d),
			);
			if (type === "publication") {
				const c = r.content as PublicationContent;
				setPubMessage(c.message ?? "");
				setPubHashtags((c.hashtags ?? []).join("\n"));
			} else {
				const c = r.content as PubliciteContent;
				setAdPrimary(c.primaryText ?? "");
				setAdHeadline(c.headline ?? "");
				setAdDescription(c.description ?? "");
				setAdCta(c.cta ?? "");
			}
			setHasResult(true);
			toast.success("Contenu généré — relisez puis enregistrez.");
		} catch (e) {
			toast.error(extractErrorMessage(e));
		} finally {
			setStreaming(false);
		}
	}

	const batchMut = useMutation({
		mutationFn: () => marketingApi.batch(basePayload()),
		onSuccess: (r) => {
			toast.success(
				`${pluriel(r.created.length, 'brouillon')} en génération${r.skipped ? ` (${pluriel(r.skipped, 'ignoré')})` : ""}.`,
			);
			void qc.invalidateQueries({ queryKey: ["marketing", "drafts"] });
		},
		onError: (e) => toast.error(extractErrorMessage(e)),
	});

	const saveMut = useMutation({
		mutationFn: () => {
			const content =
				type === "publication"
					? {
							message: pubMessage,
							hashtags: pubHashtags
								.split("\n")
								.map((h) => h.trim())
								.filter(Boolean),
						}
					: {
							primaryText: adPrimary,
							headline: adHeadline,
							description: adDescription,
							cta: adCta,
						};
			return marketingApi.saveDraft({
				type,
				productIds: items.map((i) => i.id),
				platforms: type === "publication" ? platforms : undefined,
				tone: tone || undefined,
				brief: brief.trim() || undefined,
				content,
			});
		},
		onSuccess: () => {
			toast.success("Brouillon enregistré.");
			setHasResult(false);
			setLiveText("");
			void qc.invalidateQueries({ queryKey: ["marketing", "drafts"] });
		},
		onError: (e) => toast.error(extractErrorMessage(e)),
	});

	const canGenerate = items.length > 0 && textEnabled && !streaming;

	return (
		<>
			<PageHead
				title="Générateur marketing"
				sub="Sélectionnez des produits, choisissez publication ou publicité, et laissez l'agent IA rédiger — brouillons prêts à publier."
				actions={
					<div style={{ display: "flex", gap: 8 }}>
						<Tag variant={textEnabled ? "success" : "neutral"}>
							{textEnabled ? "Texte IA connecté" : "Texte IA hors ligne"}
						</Tag>
						<Tag variant={imageEnabled ? "success" : "neutral"}>
							{imageEnabled ? "Visuels IA connectés" : "Visuels IA désactivés"}
						</Tag>
					</div>
				}
			/>

			{/* Panier */}
			<Card style={{ marginBottom: 18 }}>
				<CardHead
					title={`Panier marketing · ${items.length} produit${items.length > 1 ? "s" : ""}`}
					sub="Les produits mis en avant dans le contenu généré."
					right={
						items.length > 0 ? (
							<AdminButton variant="ghost" size="sm" onClick={clearCart}>
								Vider
							</AdminButton>
						) : undefined
					}
				/>
				<CardBody>
					{items.length === 0 ? (
						<p
							style={{
								textAlign: "center",
								padding: "20px 0",
								fontSize: 12.5,
								color: "var(--a-ink-mute)",
							}}
						>
							Panier vide. Ajoutez des produits depuis le catalogue (sélection groupée)
							ou une fiche produit.
						</p>
					) : (
						<div style={{ display: "flex", flexWrap: "wrap", gap: 10 }}>
							{items.map((it) => (
								<div
									key={it.id}
									style={{
										display: "flex",
										alignItems: "center",
										gap: 8,
										padding: "6px 10px 6px 6px",
										border: "1px solid var(--a-border)",
										borderRadius: 999,
										background: "var(--a-surface-2)",
									}}
								>
									{it.image ? (
										<img
											src={it.image}
											alt=""
											style={{
												width: 28,
												height: 28,
												borderRadius: "50%",
												objectFit: "cover",
											}}
										/>
									) : null}
									<span style={{ fontSize: 12.5, maxWidth: 200 }} className="tn">
										{it.title}
									</span>
									<button
										type="button"
										onClick={() => removeItem(it.id)}
										title="Retirer"
										style={{
											border: "none",
											background: "transparent",
											cursor: "pointer",
											color: "var(--a-ink-mute)",
											display: "inline-flex",
										}}
									>
										<AdminIcon n="close" s={13} />
									</button>
								</div>
							))}
						</div>
					)}
				</CardBody>
			</Card>

			{/* Composeur */}
			<Card style={{ marginBottom: 18 }}>
				<CardHead title="Nouveau contenu" sub="Type, mode et brief pour guider l'agent." />
				<CardBody>
					<div
						style={{
							display: "grid",
							gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
							gap: 14,
						}}
					>
						<FormRow label="Type de contenu">
							<select
								className="inp"
								value={type}
								onChange={(e) => {
									setType(e.target.value as MarketingType);
									setHasResult(false);
								}}
							>
								<option value="publication">Publication (post FB / IG)</option>
								<option value="publicite">Publicité (créative Meta)</option>
							</select>
						</FormRow>
						<FormRow label="Mode de génération">
							<select
								className="inp"
								value={mode}
								onChange={(e) => setMode(e.target.value as Mode)}
							>
								<option value="grouped">Un seul contenu regroupé</option>
								<option value="per_product">Un contenu par produit (lot)</option>
							</select>
						</FormRow>
						<FormRow label="Ton">
							<select
								className="inp"
								value={tone}
								onChange={(e) => setTone(e.target.value)}
							>
								{TONE_OPTIONS.map((o) => (
									<option key={o.value} value={o.value}>
										{o.label}
									</option>
								))}
							</select>
						</FormRow>
						{type === "publication" ? (
							<FormRow label="Plateformes">
								<div style={{ display: "flex", gap: 8 }}>
									{["facebook", "instagram"].map((p) => (
										<AdminButton
											key={p}
											size="sm"
											variant={platforms.includes(p) ? "primary" : "ghost"}
											onClick={() => togglePlatform(p)}
										>
											{p === "facebook" ? "Facebook" : "Instagram"}
										</AdminButton>
									))}
								</div>
							</FormRow>
						) : null}
					</div>

					<FormRow
						label="Brief (facultatif)"
						helper="L'angle voulu : occasion, argument clé, message à faire passer…"
					>
						<textarea
							className="inp"
							rows={2}
							value={brief}
							onChange={(e) => setBrief(e.target.value)}
							placeholder="Ex. Mettre l'accent sur la rentrée scolaire et le paiement à la livraison."
						/>
					</FormRow>

					<div
						style={{
							display: "flex",
							justifyContent: "flex-end",
							gap: 8,
							marginTop: 4,
							paddingTop: 14,
							borderTop: "1px solid var(--a-border)",
						}}
					>
						{mode === "grouped" ? (
							<AdminButton
								variant="primary"
								disabled={!canGenerate}
								onClick={() => void generateGrouped()}
							>
								<AdminIcon n="star" s={14} />
								{streaming ? "Génération…" : "Générer le texte"}
							</AdminButton>
						) : (
							<AdminButton
								variant="primary"
								disabled={!items.length || !textEnabled || batchMut.isPending}
								onClick={() => batchMut.mutate()}
							>
								<AdminIcon n="star" s={14} />
								{batchMut.isPending
									? "Lancement…"
									: `Générer ${pluriel(items.length, 'brouillon')}`}
							</AdminButton>
						)}
					</div>

					{!textEnabled ? (
						<p style={{ marginTop: 10, fontSize: 12, color: "var(--a-ink-mute)" }}>
							Le texte IA n'est pas configuré (token Claude manquant côté serveur).
						</p>
					) : null}

					{/* Aperçu live + édition (mode regroupé) */}
					{mode === "grouped" && (streaming || hasResult) ? (
						<div style={{ marginTop: 16 }}>
							{streaming ? (
								<pre
									style={{
										whiteSpace: "pre-wrap",
										fontSize: 12.5,
										background: "var(--a-surface-2)",
										border: "1px solid var(--a-border)",
										borderRadius: "var(--a-r)",
										padding: 12,
										maxHeight: 260,
										overflow: "auto",
									}}
								>
									{liveText || "…"}
								</pre>
							) : type === "publication" ? (
								<div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
									<FormRow label="Message">
										<textarea
											className="inp"
											rows={6}
											value={pubMessage}
											onChange={(e) => setPubMessage(e.target.value)}
										/>
									</FormRow>
									<FormRow label="Hashtags (un par ligne)">
										<textarea
											className="inp"
											rows={3}
											value={pubHashtags}
											onChange={(e) => setPubHashtags(e.target.value)}
										/>
									</FormRow>
								</div>
							) : (
								<div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
									<FormRow label="Texte principal">
										<textarea
											className="inp"
											rows={3}
											value={adPrimary}
											onChange={(e) => setAdPrimary(e.target.value)}
										/>
									</FormRow>
									<div
										style={{
											display: "grid",
											gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
											gap: 12,
										}}
									>
										<FormRow label="Titre">
											<input
												className="inp"
												value={adHeadline}
												onChange={(e) => setAdHeadline(e.target.value)}
											/>
										</FormRow>
										<FormRow label="Description">
											<input
												className="inp"
												value={adDescription}
												onChange={(e) => setAdDescription(e.target.value)}
											/>
										</FormRow>
										<FormRow label="Appel à l'action">
											<input
												className="inp"
												value={adCta}
												onChange={(e) => setAdCta(e.target.value)}
											/>
										</FormRow>
									</div>
								</div>
							)}

							{hasResult ? (
								<div
									style={{
										display: "flex",
										justifyContent: "flex-end",
										gap: 8,
										marginTop: 12,
									}}
								>
									<AdminButton
										variant="ghost"
										disabled={streaming}
										onClick={() => void generateGrouped()}
									>
										Régénérer
									</AdminButton>
									<AdminButton
										variant="primary"
										disabled={saveMut.isPending}
										onClick={() => saveMut.mutate()}
									>
										{saveMut.isPending ? "Enregistrement…" : "Enregistrer en brouillon"}
									</AdminButton>
								</div>
							) : null}
						</div>
					) : null}
				</CardBody>
			</Card>

			{/* Brouillons */}
			<Card>
				<CardHead
					title="Brouillons"
					sub={pluriel(draftsQuery.data?.total ?? 0, 'brouillon')}
					right={
						<AdminButton
							variant="ghost"
							size="sm"
							onClick={() => void draftsQuery.refetch()}
						>
							Rafraîchir
						</AdminButton>
					}
				/>
				<CardBody>
					{draftsQuery.isLoading ? (
						<p style={{ textAlign: "center", padding: "20px 0", fontSize: 12.5 }}>
							Chargement…
						</p>
					) : !draftsQuery.data?.data.length ? (
						<p
							style={{
								textAlign: "center",
								padding: "20px 0",
								fontSize: 12.5,
								color: "var(--a-ink-mute)",
							}}
						>
							Aucun brouillon pour l'instant.
						</p>
					) : (
						<div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
							{draftsQuery.data.data.map((d) => (
								<DraftRow key={d.id} draft={d} imageEnabled={imageEnabled} />
							))}
						</div>
					)}
				</CardBody>
			</Card>
		</>
	);
}

function DraftRow({
	draft,
	imageEnabled,
}: {
	draft: MarketingDraft;
	imageEnabled: boolean;
}) {
	const toast = useToast();
	const qc = useQueryClient();
	const [format, setFormat] = useState<VisualFormat>("square");
	const st = statusTag(draft.status);

	const invalidate = () =>
		void qc.invalidateQueries({ queryKey: ["marketing", "drafts"] });

	const imageMut = useMutation({
		mutationFn: () =>
			marketingApi.generateImage({
				productId: draft.productIds[0],
				draftId: draft.id,
				format,
				tone: draft.tone ?? undefined,
				brief: draft.brief ?? undefined,
				cta:
					draft.content && "cta" in draft.content ? draft.content.cta : undefined,
			}),
		onSuccess: (r) => {
			if (r.ok) {
				toast.success("Visuel généré.");
				invalidate();
			} else {
				toast.error(r.error ?? "Génération du visuel impossible.");
			}
		},
		onError: (e) => toast.error(extractErrorMessage(e)),
	});

	const retryMut = useMutation({
		mutationFn: () => marketingApi.retry(draft.id),
		onSuccess: invalidate,
		onError: (e) => toast.error(extractErrorMessage(e)),
	});

	const removeMut = useMutation({
		mutationFn: () => marketingApi.remove(draft.id),
		onSuccess: invalidate,
		onError: (e) => toast.error(extractErrorMessage(e)),
	});

	const preview =
		draft.content && "message" in draft.content
			? draft.content.message
			: draft.content && "primaryText" in draft.content
				? draft.content.primaryText
				: null;

	return (
		<div
			style={{
				border: "1px solid var(--a-border)",
				borderRadius: "var(--a-r)",
				padding: 14,
				display: "flex",
				flexDirection: "column",
				gap: 10,
			}}
		>
			<div
				style={{
					display: "flex",
					alignItems: "center",
					gap: 8,
					flexWrap: "wrap",
				}}
			>
				<Tag variant={draft.type === "publication" ? "info" : "warning"}>
					{draft.type === "publication" ? "Publication" : "Publicité"}
				</Tag>
				<Tag variant={st.cls}>{st.label}</Tag>
				<span style={{ fontSize: 12.5, color: "var(--a-ink-2)" }}>
					{draft.productSnapshots.map((s) => s.title).join(", ")}
				</span>
				<div style={{ flex: 1 }} />
				<span style={{ fontSize: 11, color: "var(--a-ink-mute)" }}>
					{draft.createdByLabel}
				</span>
			</div>

			{draft.error ? (
				<p style={{ fontSize: 12, color: "var(--a-danger)" }}>{draft.error}</p>
			) : null}

			{preview ? (
				<p
					style={{
						fontSize: 12.5,
						color: "var(--a-ink-2)",
						whiteSpace: "pre-wrap",
						margin: 0,
					}}
				>
					{preview.length > 320 ? `${preview.slice(0, 320)}…` : preview}
				</p>
			) : null}

			{draft.visuals.length ? (
				<div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
					{draft.visuals.map((v) => (
						<a key={v.mediaId} href={v.url} target="_blank" rel="noreferrer">
							<img
								src={v.url}
								alt=""
								style={{
									width: 96,
									height: 96,
									objectFit: "cover",
									borderRadius: "var(--a-r)",
									border: "1px solid var(--a-border)",
								}}
							/>
						</a>
					))}
				</div>
			) : null}

			<div
				style={{
					display: "flex",
					gap: 8,
					flexWrap: "wrap",
					alignItems: "center",
				}}
			>
				{imageEnabled && draft.status === "draft" ? (
					<>
						<select
							className="inp"
							style={{ width: "auto" }}
							value={format}
							onChange={(e) => setFormat(e.target.value as VisualFormat)}
						>
							{FORMAT_OPTIONS.map((o) => (
								<option key={o.value} value={o.value}>
									{o.label}
								</option>
							))}
						</select>
						<AdminButton
							size="sm"
							variant="secondary"
							disabled={imageMut.isPending}
							onClick={() => imageMut.mutate()}
						>
							<AdminIcon n="star" s={13} />
							{imageMut.isPending ? "Visuel…" : "Générer un visuel"}
						</AdminButton>
					</>
				) : null}
				{draft.status === "failed" ? (
					<AdminButton
						size="sm"
						variant="ghost"
						disabled={retryMut.isPending}
						onClick={() => retryMut.mutate()}
					>
						Relancer
					</AdminButton>
				) : null}
				<div style={{ flex: 1 }} />
				<AdminButton
					size="sm"
					variant="ghost"
					disabled={removeMut.isPending}
					onClick={() => removeMut.mutate()}
					style={{ color: "var(--a-danger)" }}
				>
					Supprimer
				</AdminButton>
			</div>
		</div>
	);
}
